import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-[#0D2340] text-white p-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white hover:bg-white/10">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Shield className="h-6 w-6" />
          <h1 className="text-lg font-semibold">Privacy Policy</h1>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
          <div>
            <p className="text-sm text-slate-500 mb-4">Last updated: February 2026</p>
            <h2 className="text-2xl font-bold text-[#0D2340] mb-4">EmergencyCard Health Privacy Policy</h2>
            <p className="text-slate-600">
              EmergencyCard Health ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your personal health information.
            </p>
          </div>

          <section>
            <h3 className="text-lg font-semibold text-[#0D2340] mb-2">1. Information We Collect</h3>
            <ul className="list-disc pl-5 space-y-2 text-slate-600">
              <li><strong>Phone Number:</strong> For account creation and login verification</li>
              <li><strong>Health Information:</strong> Medical history, prescriptions, lab reports, and daily health entries you choose to store</li>
              <li><strong>Recovery Information:</strong> Optional email, Aadhaar (last 4 digits), or Driver's License for account recovery</li>
              <li><strong>Usage Data:</strong> How you interact with the app to improve our services</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#0D2340] mb-2">2. How We Protect Your Data</h3>
            <ul className="list-disc pl-5 space-y-2 text-slate-600">
              <li><strong>AES-256 Encryption:</strong> All health data is encrypted at rest using military-grade encryption</li>
              <li><strong>Blockchain Verification:</strong> Records are verified using cryptographic hash chains for tamper-proof integrity</li>
              <li><strong>Secure Transmission:</strong> All data transfers use HTTPS/TLS encryption</li>
              <li><strong>No Third-Party Sharing:</strong> We never sell or share your health data with advertisers</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#0D2340] mb-2">3. How We Use Your Information</h3>
            <ul className="list-disc pl-5 space-y-2 text-slate-600">
              <li>To provide and maintain your health vault</li>
              <li>To enable the Aayu AI assistant to help you manage health records</li>
              <li>To generate PDF reports and QR codes for doctor sharing</li>
              <li>To send medication reminders (with your permission)</li>
              <li>To improve our services and user experience</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#0D2340] mb-2">4. Data Sharing</h3>
            <p className="text-slate-600 mb-2">Your health data is shared only when:</p>
            <ul className="list-disc pl-5 space-y-2 text-slate-600">
              <li><strong>You generate a QR code:</strong> Doctors can view (not edit) your records for a limited time</li>
              <li><strong>You export a PDF:</strong> You control who receives it</li>
              <li><strong>Legal requirement:</strong> If required by law (with notice to you when possible)</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#0D2340] mb-2">5. Your Rights</h3>
            <ul className="list-disc pl-5 space-y-2 text-slate-600">
              <li><strong>Access:</strong> View all your stored health data anytime</li>
              <li><strong>Export:</strong> Download your data as PDF</li>
              <li><strong>Delete:</strong> Remove individual records or your entire account</li>
              <li><strong>Correction:</strong> Update any incorrect information</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#0D2340] mb-2">6. Data Retention</h3>
            <p className="text-slate-600">
              We retain your data as long as your account is active. If you delete your account, all personal data is permanently removed within 30 days.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#0D2340] mb-2">7. Children's Privacy</h3>
            <p className="text-slate-600">
              EmergencyCard Health is not intended for children under 13. We do not knowingly collect data from children under 13.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#0D2340] mb-2">8. Changes to This Policy</h3>
            <p className="text-slate-600">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-[#0D2340] mb-2">9. Contact Us</h3>
            <p className="text-slate-600">
              If you have questions about this Privacy Policy, please contact us at: privacy@sehatsetu.com
            </p>
          </section>

          <div className="pt-4 border-t">
            <p className="text-sm text-slate-500 text-center">
              EmergencyCard Health - Your Digital Health Passport<br />
              Made with care in India
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
